import { useEffect, useMemo, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FieldErrors } from '../components/FieldErrors.jsx';
import { Modal } from '../components/Modal.jsx';
import { PLAN_FEATURE_FALLBACK, PLAN_FEATURE_GROUPS } from '../constants/planFeatures.js';
import { SearchableSelect } from '../components/SearchableSelect.jsx';

const emptyForm = {
  name: '',
  slug: '',
  description: '',
  price_cents: '',
  currency: 'cad',
  interval: 'month',
  stripe_price_id: '',
  is_active: true,
  sort_order: 0,
  features: [],
};

function formatInterval(interval) {
  return interval === 'year' ? 'year' : 'month';
}

function groupCatalog(catalog) {
  const groups = {};
  for (const item of catalog) {
    const g = item.group || 'other';
    if (!groups[g]) {
      groups[g] = [];
    }
    groups[g].push(item);
  }
  return groups;
}

export function SuperAdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState(PLAN_FEATURE_FALLBACK);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState(undefined);
  const [showForm, setShowForm] = useState(false);

  const closeForm = () => {
    setShowForm(false);
    setForm(emptyForm);
    setEditingId(null);
    setFieldErrors(undefined);
  };

  const catalogByGroup = useMemo(() => groupCatalog(catalog), [catalog]);

  const load = async () => {
    const [plansData, catalogData] = await Promise.all([
      apiJson('/api/super-admin/plans'),
      apiJson('/api/super-admin/plans/feature-catalog').catch(() => PLAN_FEATURE_FALLBACK),
    ]);
    setPlans(plansData);
    setCatalog(Array.isArray(catalogData) && catalogData.length ? catalogData : PLAN_FEATURE_FALLBACK);
  };

  useEffect(() => {
    load().catch((err) => setError(err.message));
  }, []);

  const toggleFeature = (key) => {
    setForm((prev) => {
      const has = prev.features.includes(key);
      return {
        ...prev,
        features: has ? prev.features.filter((f) => f !== key) : [...prev.features, key],
      };
    });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors(undefined);
    const body = {
      ...form,
      price_cents: Number(form.price_cents),
      sort_order: Number(form.sort_order),
      is_active: Boolean(form.is_active),
      features: form.features,
    };
    try {
      if (editingId) {
        await apiJson(`/api/super-admin/plans/${editingId}`, { method: 'PUT', body });
      } else {
        await apiJson('/api/super-admin/plans', { method: 'POST', body });
      }
      closeForm();
      await load();
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.body?.errors);
    }
  };

  const startEdit = (plan) => {
    setEditingId(plan.id);
    setForm({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price_cents: String(plan.price_cents),
      currency: plan.currency,
      interval: plan.interval || 'month',
      stripe_price_id: plan.stripe_price_id || '',
      is_active: plan.is_active,
      sort_order: plan.sort_order ?? 0,
      features: Array.isArray(plan.features) ? [...plan.features] : [],
    });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setFieldErrors(undefined);
    setShowForm(true);
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this plan?')) {
      return;
    }
    await apiJson(`/api/super-admin/plans/${id}`, { method: 'DELETE' });
    await load();
  };

  return (
    <div className="stack">
      <div
        className="row-actions"
        style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.75rem' }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 0 }} data-tour="page-title">
          Subscription plans
        </h2>
        <button type="button" className="btn-primary" onClick={openCreate} data-tour="plans-features">
          New plan
        </button>
      </div>
      <p className="muted">
        Configure billing period (monthly or yearly) and which capabilities organizations receive with each plan.
      </p>
      {error ? <p className="err">{error}</p> : null}

      <Modal
        open={showForm}
        title={editingId ? 'Edit plan' : 'New plan'}
        onClose={closeForm}
        size="lg"
      >
        <form onSubmit={onSubmit} className="stack">
          <label className="field">
            <span>Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </label>
          <label className="field">
            <span>Slug</span>
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required />
          </label>
          <label className="field">
            <span>Description</span>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </label>
          <div className="row-actions" style={{ gap: '1rem', flexWrap: 'wrap' }}>
            <label className="field" style={{ flex: '1 1 8rem' }}>
              <span>Price (cents)</span>
              <input
                type="number"
                min="0"
                value={form.price_cents}
                onChange={(e) => setForm({ ...form, price_cents: e.target.value })}
                required
              />
            </label>
            <label className="field" style={{ flex: '0 1 6rem' }}>
              <span>Currency</span>
              <input
                value={form.currency}
                maxLength={3}
                onChange={(e) => setForm({ ...form, currency: e.target.value.toLowerCase() })}
              />
            </label>
            <label className="field" style={{ flex: '0 1 8rem' }}>
              <span>Billing period</span>
              <SearchableSelect value={form.interval} onChange={(e) => setForm({ ...form, interval: e.target.value })}>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </SearchableSelect>
            </label>
            <label className="field" style={{ flex: '0 1 5rem' }}>
              <span>Sort</span>
              <input
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
              />
            </label>
          </div>
          <label className="field">
            <span>Stripe price ID</span>
            <input
              value={form.stripe_price_id}
              onChange={(e) => setForm({ ...form, stripe_price_id: e.target.value })}
              placeholder="price_..."
            />
          </label>
          <label className="field checkbox-row">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            <span>Active (visible for checkout)</span>
          </label>

          <fieldset className="stack" style={{ border: 'none', padding: 0, margin: 0 }}>
            <legend style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Plan features</legend>
            {Object.entries(catalogByGroup).map(([groupKey, items]) => (
              <div key={groupKey} className="stack" style={{ marginBottom: '0.75rem' }}>
                <p className="muted small" style={{ margin: 0 }}>
                  {PLAN_FEATURE_GROUPS[groupKey] ?? groupKey}
                </p>
                <ul className="list-plain">
                  {items.map((item) => (
                    <li key={item.key}>
                      <label className="checkbox-row">
                        <input
                          type="checkbox"
                          checked={form.features.includes(item.key)}
                          onChange={() => toggleFeature(item.key)}
                        />
                        <span>
                          <strong>{item.label}</strong>
                          {item.description ? (
                            <span className="muted small" style={{ display: 'block' }}>
                              {item.description}
                            </span>
                          ) : null}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </fieldset>

          <FieldErrors errors={fieldErrors} />
          <div className="row-actions">
            <button type="button" className="btn-secondary" onClick={closeForm}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingId ? 'Save' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      <ul className="list-plain" data-tour="plans-list">
        {plans.map((plan) => (
          <li key={plan.id} className="card-panel" style={{ marginBottom: '0.75rem' }}>
            <strong>{plan.name}</strong> ({plan.slug}) — {(plan.price_cents / 100).toFixed(2)}{' '}
            {plan.currency?.toUpperCase()} / {formatInterval(plan.interval)}
            {plan.is_active ? '' : ' · inactive'}
            <p className="muted small" style={{ margin: '0.35rem 0 0' }}>
              Features:{' '}
              {Array.isArray(plan.features) && plan.features.length > 0
                ? plan.features.join(', ')
                : plan.features === null
                  ? 'all (legacy default)'
                  : 'none selected'}
            </p>
            <div className="row-actions" style={{ marginTop: '0.5rem' }}>
              <button type="button" className="btn-link" onClick={() => startEdit(plan)}>
                Edit
              </button>
              <button type="button" className="btn-secondary" onClick={() => remove(plan.id)}>
                Delete
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
